<template>
  <Transition name="fade">
    <div v-if="hint" ref="hintContainer" class="hintContainer hintfade">
      <v-card dark color="#5E00EB" :width="HINT_CARD_WIDTH">
        <v-card-title class="pb-1 hintTitle">{{ hint.Title }}</v-card-title>
        <v-img
          v-if="hint.Media && hint.Media.Type === 'image'"
          contain
          :src="hint.Media.Link"
        ></v-img>
        <v-card-text class="pa-0" v-if="hint.Media && hint.Media.Type === 'video'">
          <iframe
            width="300"
            allowfullscreen
            frameborder="0"
            :src="
              hint.Media.Type === 'video'
                ? `https://www.youtube.com/embed/${hint.Media.Link}`
                : hint.Media.Link
            "
          >
          </iframe>
        </v-card-text>
        <v-card-text class="py-1 hintDescription">{{ hint.Description }}</v-card-text>
        <v-card-text v-if="hint.LearnMore" class="py-1 hintDescription">
          <a
            :href="hint.LearnMore"
            target="_blank"
            style="color: white; font-style: italic;"
            @click="
              guibus_publish('dispatch:beacon', {
                eventId: `welcome-hint_modal_${hint.Title}_learn_more_link-click`,
              })
            "
            >Learn More</a
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-row class="wrap" justify="end" no-gutters>
            <v-btn light color="white" @click="handleButtonClick">{{
              hint.ButtonText ? hint.ButtonText : 'Got It!'
            }}</v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'hintModal',
  props: {
    currentHint: Object,
  },
  data() {
    return {
      HINT_CARD_WIDTH: 300,
      hintTargetElementRect: undefined,
      hint: this.currentHint,
    }
  },
  watch: {
    currentHint: {
      handler(val) {
        if (val) {
          this.hint = val
          this.showSelectedHint()
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleButtonClick() {
      this.guibus_publish('dispatch:beacon', {
        eventId: `welcome-hint_modal_close_button-click`,
      })
      this.$emit('closeHint', this.hint.HintID)
      this.resetCurrentHint()
    },
    showSelectedHint() {
      this.$nextTick(() => {
        this.hintTargetElementRect = document
          .querySelectorAll(`.${this.hint.TargetElementClass}`)[0]
          ?.getBoundingClientRect()
        // if no element found that relates to current hint, skip hint and continue processing
        if (!this.hintTargetElementRect && this.hint.HintPosition !== 'window-center') {
          this.resetCurrentHint()
          return
        }
        this.$nextTick(() => {
          this.setHintPosition()
        })
      })
    },
    // position hint and indicator arrow depending on hint position (arrow indicator directions are opposite of position,
    // eg. a hint positioned 'top' is above the element it is indicating, therefore it needs a 'bottom' facing arrow)
    setHintPosition() {
      // Set hint above campaign guide (consequently header) only for first hint
      if (this.hint.HintID === 1) {
        this.$refs.hintContainer.style.zIndex = '1000'
      } else {
        this.$refs.hintContainer.style.zIndex = '998'
      }
      switch (this.hint.HintPosition) {
        case 'window-center':
          this.$refs.hintContainer.style.top = `calc(100vh / 2 - ${this.$refs.hintContainer
            .offsetHeight / 2}px)`
          this.$refs.hintContainer.style.left = `calc(100vw / 2 - ${this.$refs.hintContainer
            .offsetWidth / 2}px)`
          this.$refs.hintContainer.classList.add('noArrow')
          break
        case 'top':
          this.$refs.hintContainer.style.bottom = `calc(100vh - ${this.hintTargetElementRect.top -
            18}px)`
          this.$refs.hintContainer.style.left = `${this.hintTargetElementRect.left}px`
          this.$refs.hintContainer.classList.add('bottomArrow')
          break
        case 'bottom':
          this.$refs.hintContainer.style.top = `${this.hintTargetElementRect.bottom + 25}px`
          this.$refs.hintContainer.style.left = `${this.hintTargetElementRect.left}px`
          this.$refs.hintContainer.classList.add('topArrow')
          break
        case 'left':
          this.$refs.hintContainer.style.top = `${this.hintTargetElementRect.top}px`
          this.$refs.hintContainer.style.left = `${this.hintTargetElementRect.left -
            this.HINT_CARD_WIDTH -
            18}px`
          this.$refs.hintContainer.classList.add('rightArrow')
          break
        case 'right':
          this.$refs.hintContainer.style.top = `${this.hintTargetElementRect.top}px`
          this.$refs.hintContainer.style.left = `${this.hintTargetElementRect.right + 18}px`
          this.$refs.hintContainer.classList.add('leftArrow')
          break
        // all hints other than first hint will be below header in z-index, thus this position is necessary
        // for hints that need to indicate header elements on the far right
        case 'bottom-right':
          this.$refs.hintContainer.style.top = `${this.hintTargetElementRect.bottom + 25}px`
          this.$refs.hintContainer.style.right = `calc(100vw - ${this.hintTargetElementRect.right +
            18}px)`
          this.$refs.hintContainer.classList.add('topRightArrow')
          break
      }
    },
    resetCurrentHint() {
      this.hint = undefined
      if (this.$refs.hintContainer) {
        this.$refs.hintContainer.style.top = ''
        this.$refs.hintContainer.style.bottom = ''
        this.$refs.hintContainer.style.right = ''
        // This offset is mainly for the last hint as animation fade is
        // still applied with the component placed in the corner. Instead
        // we just move the component out of view while it transitions out.
        this.$refs.hintContainer.style.left = '-1500px'
      }
      this.hintTargetElementRect = undefined
      this.$refs.hintContainer?.classList.remove('topArrow')
      this.$refs.hintContainer?.classList.remove('bottomArrow')
      this.$refs.hintContainer?.classList.remove('leftArrow')
      this.$refs.hintContainer?.classList.remove('rightArrow')
      this.$refs.hintContainer?.classList.remove('topRightArrow')
      this.$refs.hintContainer?.classList.remove('noArrow')
    },
  },
}
</script>

<style lang="scss" scoped>
.hintContainer {
  position: fixed;
  color: white;
  .hintTitle {
    word-break: break-word;
  }
  .hintDescription {
    max-height: 250px;
    overflow-y: auto;
    color: white !important;
  }
  &:after {
    content: ' ';
    position: absolute;
    border-width: 10px;
    border-style: solid;
    box-shadow: -2px -2px 2px 0 rgba(178, 178, 178, 0.4);
    color: #5e00eb;
  }
  &.noArrow:after {
    display: none;
  }
  &.rightArrow:after {
    top: 2.5%;
    left: 100%;
    transform: rotate(135deg);
    margin-left: -11px;
  }
  &.leftArrow:after {
    top: 2.5%;
    right: 100%;
    transform: rotate(315deg);
    margin-right: -11px;
  }
  &.topArrow:after {
    bottom: 100%;
    left: 5%;
    transform: rotate(45deg);
    margin-bottom: -11px;
  }
  &.bottomArrow:after {
    top: 100%;
    left: 5%;
    transform: rotate(225deg);
    margin-top: -11px;
  }
  &.topRightArrow:after {
    bottom: 100%;
    right: 5%;
    transform: rotate(45deg);
    margin-bottom: -11px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
